import { cn } from "@/lib/utils";

/**
 * Brand badge for an AI provider. We render a branded monogram (brand
 * colour + glyph) rather than the vendors' trademarked logo artwork, so
 * the picker reads cleanly without bundling copyrighted marks. Swap in
 * official SVGs here later if the assets + usage rights are in place.
 */

type ProviderKey =
  | "openai"
  | "anthropic"
  | "bedrock"
  | "google-vertex"
  | "alibaba";

const BRAND: Record<
  ProviderKey,
  { bg: string; fg: string; glyph: string; glyphClass?: string }
> = {
  openai: { bg: "#10A37F", fg: "#ffffff", glyph: "O" },
  anthropic: { bg: "#D97757", fg: "#ffffff", glyph: "A" },
  bedrock: { bg: "#FF9900", fg: "#1a1a1a", glyph: "aws", glyphClass: "text-[8px] lowercase tracking-tight" },
  "google-vertex": { bg: "#4285F4", fg: "#ffffff", glyph: "G" },
  alibaba: { bg: "#FF6A00", fg: "#ffffff", glyph: "Q" },
};

interface ProviderLogoProps {
  provider: string;
  className?: string;
}

export function ProviderLogo({ provider, className }: ProviderLogoProps) {
  const brand = BRAND[provider as ProviderKey];
  if (!brand) {
    return (
      <span
        className={cn(
          "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-ocean-100 text-[11px] font-bold text-ocean-600",
          className
        )}
        aria-hidden="true"
      >
        {provider.slice(0, 1).toUpperCase()}
      </span>
    );
  }
  return (
    <span
      className={cn(
        "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-[11px] font-bold leading-none",
        brand.glyphClass,
        className
      )}
      style={{ background: brand.bg, color: brand.fg }}
      aria-hidden="true"
    >
      {brand.glyph}
    </span>
  );
}
